'use client'

import { Suspense, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { Mail, KeyRound, CheckCircle, AlertCircle } from 'lucide-react'
import { FglHeader } from '@/components/layout/fgl-header'
import { useTranslation } from '@/i18n/client'
import {
  LenisProvider,
  Reveal,
  Magnetic,
  LivingThermal,
  GliderBackdrop, GliderOrbit,
} from '@/components/award'

export default function ResetPasswordPage() {
  return (
    <LenisProvider>
      <div data-award="true" className="relative min-h-screen flex flex-col" style={{ background: 'var(--award-ink)' }}>
        <FglHeader />
      <GliderBackdrop />
        <Suspense fallback={
          <div className="flex-1 flex items-center justify-center pt-[84px]">
            <div
              className="w-8 h-8 rounded-full border-2 border-transparent animate-spin"
              style={{ borderTopColor: 'var(--award-gold)', borderRightColor: 'var(--award-gold)' }}
            />
          </div>
        }>
          <ResetPasswordContent />
        </Suspense>
      </div>
    </LenisProvider>
  )
}

function ResetPasswordContent() {
  const { t } = useTranslation()
  const searchParams = useSearchParams()
  const token = searchParams.get('token')
  const mode: 'forgot' | 'reset' = token ? 'reset' : 'forgot'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setMessage(null)
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const data = await res.json()
      setMessage({ type: 'success', text: data.message || t('resetPassword.linkSent') })
    } catch {
      setMessage({ type: 'error', text: t('resetPassword.sendError') })
    } finally {
      setLoading(false)
    }
  }

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setMessage(null)
    if (password !== confirmPassword) {
      setMessage({ type: 'error', text: t('resetPassword.passwordsMismatch') })
      setLoading(false)
      return
    }
    if (password.length < 8) {
      setMessage({ type: 'error', text: t('resetPassword.passwordTooShort') })
      setLoading(false)
      return
    }
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      })
      const data = await res.json()
      if (res.ok) setMessage({ type: 'success', text: data.message || t('resetPassword.passwordChanged') })
      else setMessage({ type: 'error', text: data.error || t('resetPassword.resetError') })
    } catch {
      setMessage({ type: 'error', text: t('resetPassword.genericError') })
    } finally {
      setLoading(false)
    }
  }

  const Icon = mode === 'forgot' ? Mail : KeyRound

  return (
    <div className="relative flex-1 flex items-center justify-center overflow-hidden pt-[84px] pb-16">
      <div className="relative z-10 max-w-[600px] mx-auto px-6 md:px-12 w-full">
        <div className="text-center mb-8">
          <Reveal>
            <div className="flex items-center justify-center gap-3 mb-6 flex-wrap">
              <span className="award-eyebrow">Konto · Sicherheit</span>
              <span className="h-px w-16 bg-[var(--award-line-hi)]" />
              <span className="award-label">{mode === 'forgot' ? 'Passwort vergessen' : 'Neues Passwort'}</span>
            </div>
          </Reveal>

          <Reveal delay={1}>
            <div
              className="inline-flex w-20 h-20 rounded-full items-center justify-center mb-6"
              style={{
                background: 'var(--award-gold-soft)',
                border: '2px solid var(--award-gold)',
                boxShadow: '0 0 32px var(--award-gold-glow)',
              }}
            >
              <Icon className="w-10 h-10" style={{ color: 'var(--award-gold)' }} />
            </div>
          </Reveal>

          <Reveal delay={2}>
            <h1 className="award-h-lg">
              {mode === 'forgot' ? t('resetPassword.forgotTitle') : t('resetPassword.resetTitle')}
            </h1>
          </Reveal>
        </div>

        <Reveal delay={3}>
          <div
            className="rounded-[20px] p-7 md:p-9"
            style={{ background: 'var(--award-ink-2)', border: '1px solid var(--award-line)' }}
          >
            {message && (
              <div
                className="mb-6 p-4 rounded-[12px] flex items-start gap-3"
                style={{
                  background: message.type === 'success'
                    ? 'color-mix(in oklch, #16a34a 12%, transparent)'
                    : 'color-mix(in oklch, #dc2626 12%, transparent)',
                  border: `1px solid ${message.type === 'success' ? '#16a34a' : '#dc2626'}`,
                }}
              >
                {message.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#16a34a' }} />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#dc2626' }} />
                )}
                <span className="text-[14px]" style={{ color: 'var(--award-cirrus)' }}>
                  {message.text}
                </span>
              </div>
            )}

            {mode === 'forgot' ? (
              <form onSubmit={handleForgot} className="space-y-5">
                <p className="text-[14px] leading-relaxed mb-2" style={{ color: 'var(--award-cirrus-2)' }}>
                  {t('resetPassword.forgotDesc')}
                </p>
                <div>
                  <label className="award-label block mb-2">{t('resetPassword.email')}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="award-input w-full"
                    placeholder={t('resetPassword.emailPlaceholder')}
                  />
                </div>
                <Magnetic>
                  <button type="submit" disabled={loading} className="award-btn award-btn-primary w-full justify-center">
                    {loading ? t('resetPassword.sending') : t('resetPassword.sendLink')}
                    {!loading && <span aria-hidden>→</span>}
                  </button>
                </Magnetic>
              </form>
            ) : (
              <form onSubmit={handleReset} className="space-y-5">
                <div>
                  <label className="award-label block mb-2">{t('resetPassword.newPassword')}</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="award-input w-full"
                    placeholder={t('resetPassword.minChars')}
                  />
                </div>
                <div>
                  <label className="award-label block mb-2">{t('resetPassword.confirmPassword')}</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="award-input w-full"
                    placeholder={t('resetPassword.confirmPlaceholder')}
                  />
                </div>
                <Magnetic>
                  <button type="submit" disabled={loading} className="award-btn award-btn-primary w-full justify-center">
                    {loading ? t('resetPassword.saving') : t('resetPassword.changePassword')}
                    {!loading && <span aria-hidden>→</span>}
                  </button>
                </Magnetic>
              </form>
            )}

            <div className="mt-6 text-center">
              <Link href="/login" className="award-link text-[13px]">
                {t('resetPassword.backToLogin')}
              </Link>
            </div>
          </div>
        </Reveal>

        <style jsx global>{`
          .award-input {
            font-family: var(--font-sans);
            font-size: 14px;
            padding: 12px 16px;
            border-radius: 12px;
            background: var(--award-ink-3);
            border: 1px solid var(--award-line);
            color: var(--award-cirrus);
            outline: none;
            transition: border-color 0.2s, background 0.2s;
          }
          .award-input:focus {
            border-color: var(--award-gold);
            background: var(--award-ink-2);
          }
          .award-input::placeholder { color: var(--award-cirrus-3); }
        `}</style>
      </div>
    </div>
  )
}
